import './FilterBar.scss'
import { useLayoutEffect, useRef, type ComponentProps, type ReactNode } from 'react'
import { clx } from '../clx'
import { LabelPlacementContext, type LabelPlacement } from '../Field'
import { useTableFrame } from '../TableFrame/context'
import { alignFilters } from './align'

export interface FilterBarProps extends Omit<
  ComponentProps<'form'>,
  'role' | 'aria-label' | 'onSubmit'
> {
  /** Accessible name of the search landmark, e.g. "Filter tenants". */
  label: string
  /** The filter Fields. Filters apply as they change; there is no submit. */
  children: ReactNode
  /** Trailing actions, e.g. "Clear filters" and Refresh. */
  actions?: ReactNode
  /** Label placement for every Field inside (ADR 0020). Default `top`. */
  labelPlacement?: LabelPlacement
}

/** Ties a filter to the table column it filters, for column alignment. */
export function FilterBarItem({
  column,
  children
}: {
  column?: string | undefined
  children: ReactNode
}) {
  return (
    <div className="filter-bar__item" data-filter-column={column}>
      {children}
    </div>
  )
}

/**
 * Always-visible list filters (SPEC §5.2): a search landmark that never
 * submits — Enter in a field does nothing — with the fields in a responsive
 * grid and an optional actions slot.
 *
 * In a TableFrame's toolbar, from `desktop`, filters wrapped in
 * `FilterBarItem column="…"` line up with their columns when they fit, and
 * the frame gives those columns room for them (ADR 0023, 0025).
 */
export function FilterBar({
  label,
  children,
  actions,
  labelPlacement = 'top',
  className,
  ...rest
}: FilterBarProps) {
  const frame = useTableFrame()
  const columns = frame?.columns
  const setFilteredColumns = frame?.setFilteredColumns
  const reported = useRef('')
  const rootRef = useRef<HTMLFormElement>(null)
  const fieldsRef = useRef<HTMLDivElement>(null)
  const actionsRef = useRef<HTMLDivElement>(null)

  // Re-align after every render, when a filter appears or goes (a child that
  // renders itself later, e.g. once its options load), and on resize.
  useLayoutEffect(() => {
    const root = rootRef.current
    const fields = fieldsRef.current
    if (!root || !fields) return
    const run = () => {
      const keys = Array.from(fields.querySelectorAll<HTMLElement>('[data-filter-column]')).map(
        (el) => el.dataset.filterColumn ?? ''
      )
      if (keys.join('|') !== reported.current) {
        reported.current = keys.join('|')
        setFilteredColumns?.(keys)
      }
      alignFilters(root, fields, actionsRef.current, columns)
    }
    run()
    const mutations = new MutationObserver(run)
    mutations.observe(fields, { childList: true })
    const resize = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(run)
    resize?.observe(root)
    return () => {
      mutations.disconnect()
      resize?.disconnect()
    }
  })

  return (
    <form
      noValidate
      {...rest}
      ref={rootRef}
      role="search"
      aria-label={label}
      className={clx(
        'filter-bar',
        actions != null && 'filter-bar--has-actions',
        labelPlacement === 'floating' && 'filter-bar--has-floating-labels',
        className
      )}
      onSubmit={(event) => {
        event.preventDefault()
      }}
    >
      <div ref={fieldsRef} className="filter-bar__fields">
        <LabelPlacementContext value={labelPlacement}>{children}</LabelPlacementContext>
      </div>
      {actions != null && (
        <div ref={actionsRef} className="filter-bar__actions">
          {actions}
        </div>
      )}
    </form>
  )
}
